/**
 * toErrorText — coerce ANY error shape into a renderable string.
 *
 * Used everywhere we `setError(...)` so React never receives an object
 * as a child (React error #31: "Objects are not valid as a React child").
 *
 * Handles these common shapes:
 *   • plain string                                     → returned as-is
 *   • FastAPI / Pydantic 422 array of error objects   → joined msg fields
 *     e.g. [{type, loc, msg, input, ctx, url}, ...]
 *   • Single Pydantic error object                     → its .msg
 *   • Generic `{ message: "..." }`                     → .message
 *   • Axios error object                               → .response.data.detail
 *   • Anything else                                    → fallback string
 */
export const toErrorText = (input, fallback = 'تعذّرت العملية. حاول مجدداً.') => {
  if (input == null) return fallback;
  // 1. Axios error → unwrap once.
  if (typeof input === 'object' && input?.response?.data !== undefined) {
    return toErrorText(input.response.data.detail ?? input.response.data, fallback);
  }
  // 2. Plain string.
  if (typeof input === 'string') return input;
  // 3. Pydantic 422: detail is an Array of {type, loc, msg, input, ctx, url}.
  if (Array.isArray(input)) {
    const parts = input
      .map((e) => {
        if (typeof e === 'string') return e;
        if (e && typeof e === 'object') {
          const msg = e.msg || e.message || '';
          const loc = Array.isArray(e.loc) ? e.loc.filter((p) => p !== 'body').join('.') : '';
          return loc ? `${loc}: ${msg}` : msg;
        }
        return '';
      })
      .filter(Boolean);
    return parts.length ? parts.join(' • ') : fallback;
  }
  // 4. Generic object — try common shapes.
  if (typeof input === 'object') {
    if (typeof input.message === 'string') return input.message;
    if (typeof input.msg === 'string') return input.msg;
    if (typeof input.detail === 'string') return input.detail;
    if (input.detail) return toErrorText(input.detail, fallback);
    try {
      const s = JSON.stringify(input);
      return s === '{}' ? fallback : s;
    } catch (_) { return fallback; }
  }
  return String(input) || fallback;
};

export default toErrorText;
